/**
 * Eine Rückfrage bleibt oben stehen, bis Martin reagiert. (Ticket 2660,
 * 27.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, 26.09., auf der Autobahn: *„gerade eine Meldung, Rückfrage an
 * mich, allerdings, da ich gerade auf der Autobahn bin, kann ich das nicht
 * lesen, da wäre gut, wenn ich einen Abspielbutton hätte"*. Gewünscht:
 * ein Ton, damit er es merkt; die Frage sichtbar, bis er reagiert, statt im
 * Verlauf zu verschwinden; ein Knopf, der sie vorliest.
 *
 * ## Was als Rückfrage gilt
 *
 * ⚠️ Nur was das MODELL als Rückfrage markiert hat — kein Fragezeichen,
 *    keine Wortliste. Zwei Marken gibt es, beide schon vor diesem Ticket:
 *    - das Werkzeug `ask_confirm` (rueckfrageAusKarte),
 *    - die feste Zeile „RÜCKFRAGE: …" eines Hintergrundlaufs, aus der der
 *      Reaper eine System-Zeile im Chat macht (rueckfrageAusZeile,
 *      shared/rueckfrage.ts).
 *
 * ## Offen ist die jüngste, nach der Martin nichts gesagt hat
 *
 * ⚠️ 28.09.2026 (Prüfbefund zu pre.3403): Die Karte hing nur am Chat-Strom
 *    und an der Live-Karte. Dieselbe Rückfrage kommt aber auch über den
 *    Abgleich mit dem Server an — nach der Rückkehr aus dem Hintergrund,
 *    nach einem Verbindungsabriss, beim Kaltstart, nach dem Tipp auf die
 *    Push-Meldung. Mit gesperrtem Telefon im Auto ist das sogar der
 *    wahrscheinliche Weg, und dort gab es weder Karte noch Ton.
 *    Jetzt liest `offeneRueckfrage` die Zeilen, die gerade ankamen, und
 *    der Strom und der Abgleich gehen denselben Weg (app.ts,
 *    heftOffeneRueckfrageAn).
 *
 * ## Ein Besitzer
 *
 * Die angeheftete Karte gehört diesem Modul, und es gibt höchstens EINE.
 * Die Karte im Verlauf bleibt als Beleg: oben steht ein Spiegel, dessen
 * Antwortknöpfe die Knöpfe der Karte im Verlauf drücken — so gibt es EINEN
 * Weg, eine Rückfrage zu beantworten, und unten ist danach dieselbe Wahl
 * markiert wie oben.
 *
 * ## Ja/Nein steht nur an EINER Stelle (pre.3598, 02.10.2026)
 *
 * ⚠️ Bis pre.3595 standen die Knöpfe zweimal da: oben im Spiegel UND in der
 *    Karte im Verlauf — Martin: *„Es braucht aber nur eines.“* Solange die
 *    Karte oben steht, zeigt die im Verlauf nur eine ruhige Zeile „Offen“
 *    (Stand `oben`, tool-cards.ts); ihre Knöpfe bleiben im Baum, versteckt
 *    für Auge, Tastatur und Screenreader, und werden vom Spiegel gedrückt.
 *    Geht die Karte oben weg, ohne dass geantwortet wurde (Wegtippen,
 *    Martins nächster Zug, eine neue Rückfrage), bekommt die Karte im
 *    Verlauf ihre Knöpfe zurück — eine offene Frage bleibt beantwortbar.
 *    Nach der Antwort steht dort „Beantwortet: Ja“, auch nach dem Neuladen
 *    (beantworteteKarten, Abgleich mit dem Server).
 *
 * Weg ist sie, wenn Martin antwortet (der nächste Zug, eroeffneZug in
 * app.ts), wenn er sie wegtippt, oder wenn eine neue Rückfrage sie ablöst.
 * Welche zuletzt so ging, merkt sich dieses Modul (`ERLEDIGT`) — sonst
 * brächte der nächste Abgleich die weggetippte Frage mit Ton zurück.
 */
import { rueckfrageAusMeldung } from "../../shared/rueckfrage.js";
import {
  beantworteConfirmKarte,
  bestaetigungsFrage,
  confirmKarteVon,
  setzeConfirmStand,
} from "./tool-cards.js";
import { zeichen } from "./zeichen.js";

export interface Rueckfrage {
  /**
   * Woran dieselbe Rückfrage wiedererkannt wird, egal auf welchem Weg sie
   * kommt: `karte|<callId>` oder `zeile|<Chat-Zeile>`.
   */
  schluessel: string;
  frage: string;
  /** Woher sie kommt, wenn nicht aus dem Gespräch: „Selbstverbesserung · Ticket #2503". */
  herkunft?: string;
  /** Die Antwortknöpfe der Karte im Verlauf; oben stehen Spiegel, die sie drücken. */
  antworten?: readonly HTMLButtonElement[];
}

interface RueckfrageUmgebung {
  /** Der Vorlese-Knopf der Blasen (makePlayButton) — derselbe Vorlese-Weg. */
  vorleseKnopf: (text: string) => HTMLButtonElement;
  /** Der Hinweiston. */
  ton: () => void;
}

/** Eine Werkzeugkarte, wie Live-Weg, Strom und Verlauf sie tragen. */
interface KartenDaten {
  tool: string;
  callId: string;
  isError: boolean;
  result: unknown;
}

/** Eine Zeile des Gesprächs — aus dem Chat-Strom oder vom Server. */
export interface VerlaufsZeile {
  role: string;
  content: string;
  /** Im Chat-Strom tragen die Zeilen ihre Karten direkt … */
  toolCards?: unknown;
  /** … vom Server unter `metadata`. */
  metadata?: { toolCards?: unknown } | null;
}

/** Die Rückfrage einer Werkzeugkarte (`ask_confirm`), sonst `null`. */
export function rueckfrageAusKarte(card: KartenDaten): Rueckfrage | null {
  // Gescheitert, auch wenn das Ergebnis eine Frage trägt — dann wurde nichts gefragt.
  if (card.tool !== "ask_confirm" || card.isError) return null;
  const frage = bestaetigungsFrage(card.result);
  return frage ? { schluessel: `karte|${card.callId || frage}`, frage } : null;
}

/**
 * Die Rückfrage eines Hintergrundlaufs, sonst `null`.
 *
 * ⚠️ Nur die System-Zeile zählt: Zitiert Vespera oder Martin die Zeile, ist
 *    das keine neue Rückfrage.
 */
function rueckfrageAusZeile(zeile: VerlaufsZeile): Rueckfrage | null {
  if (zeile.role !== "system") return null;
  const r = rueckfrageAusMeldung(zeile.content);
  return r ? { schluessel: `zeile|${zeile.content.trim()}`, ...r } : null;
}

function kartenDer(zeile: VerlaufsZeile): KartenDaten[] {
  const roh = Array.isArray(zeile.toolCards) ? zeile.toolCards : zeile.metadata?.toolCards;
  if (!Array.isArray(roh)) return [];
  return roh.map((c: Partial<KartenDaten>) => ({
    tool: String(c?.tool ?? ""),
    callId: String(c?.callId ?? ""),
    isError: Boolean(c?.isError),
    result: c?.result,
  }));
}

/**
 * Die offene Rückfrage unter diesen Zeilen (älteste zuerst): die jüngste,
 * nach der Martin nichts mehr gesagt hat — sonst `null`.
 *
 * Eine spätere Meldung oder Antwort von Vespera schließt sie nicht; erst
 * eine Zeile von Martin. Dieselbe Regel wie live, wo sein nächster Zug sie
 * nimmt (eroeffneZug).
 *
 * `callId` ist gesetzt, wenn sie von einer Karte stammt — deren Knöpfe
 * braucht der Spiegel oben.
 */
export function offeneRueckfrage(
  zeilen: readonly VerlaufsZeile[],
): (Rueckfrage & { callId?: string }) | null {
  for (let i = zeilen.length - 1; i >= 0; i--) {
    const zeile = zeilen[i];
    if (zeile.role === "user") return null;
    const karten = kartenDer(zeile);
    for (let k = karten.length - 1; k >= 0; k--) {
      const r = rueckfrageAusKarte(karten[k]);
      if (r) return { ...r, callId: karten[k].callId };
    }
    const r = rueckfrageAusZeile(zeile);
    if (r) return r;
  }
  return null;
}

/** Die Antwortknöpfe einer Rückfrage-Karte im Verlauf. */
export function antwortKnoepfe(karte: HTMLElement | undefined): HTMLButtonElement[] {
  return karte ? Array.from(karte.querySelectorAll<HTMLButtonElement>(".tool-actions button")) : [];
}

/*
 * Die zuletzt gelöste Rückfrage. Es genügt EINE: Kandidat ist immer nur die
 * jüngste, und eine neue löst die alte ab. Im Gerätespeicher, damit auch
 * ein Kaltstart sie nicht zurückbringt; fehlt der Speicher, gilt sie bis
 * zum Neuladen.
 */
const ERLEDIGT = "voice-v8.rueckfrage-erledigt";
let erledigtOhneSpeicher: string | null = null;

function erledigt(): string | null {
  try {
    return localStorage.getItem(ERLEDIGT);
  } catch {
    return erledigtOhneSpeicher;
  }
}

function merkeErledigt(schluessel: string): void {
  try {
    localStorage.setItem(ERLEDIGT, schluessel);
  } catch {
    erledigtOhneSpeicher = schluessel;
  }
}

interface Angeheftet {
  karte: HTMLElement;
  schluessel: string;
  /** Die Knöpfe unten, die die Spiegel oben drücken. */
  ziele: readonly HTMLButtonElement[];
}

let angeheftet: Angeheftet | null = null;

/** Den Stand der Verlaufskarte setzen, zu der diese Knöpfe gehören. */
function stelleVerlaufskarten(ziele: readonly HTMLButtonElement[], stand: "frei" | "oben"): void {
  setzeConfirmStand(confirmKarteVon(ziele[0]), stand);
}

/** Die angeheftete Karte, oder `null`. */
export function angehefteteRueckfrage(): HTMLElement | null {
  return angeheftet?.karte.isConnected ? angeheftet.karte : null;
}

/** Die Karte nehmen — Martin hat reagiert, oder eine neue löst sie ab. */
export function loeseRueckfrage(): void {
  if (!angeheftet) return;
  merkeErledigt(angeheftet.schluessel);
  // Die Knöpfe unten wieder zeigen (eine beantwortete Karte bleibt ruhig).
  stelleVerlaufskarten(angeheftet.ziele, "frei");
  angeheftet.karte.remove();
  angeheftet = null;
}

/**
 * Der Platz oben, einmal angelegt.
 *
 * ⚠️ 28.09.2026 (Prüfbefund zu pre.3403): Hier stand „über dem Verlauf, in
 *    #view-chat". Auf Zuhause — wo die App öffnet —, in Mein Alltag und auf
 *    jeder anderen Seite klang der Ton, zu sehen war nichts. Jetzt ein
 *    fester Streifen direkt im <body>: außerhalb von #app (eigene
 *    Stapelebene, z-index 1), damit er auch über den Vollseiten liegt
 *    (style.css, .rueckfrage-platz).
 */
function platz(): HTMLElement {
  let p = document.querySelector<HTMLElement>("body > .rueckfrage-platz");
  if (!p) {
    p = document.createElement("div");
    p.className = "rueckfrage-platz";
    document.body.appendChild(p);
  }
  return p;
}

/** Ein Knopf oben, der die Antwort unten wählt. */
function spiegelKnopf(original: HTMLButtonElement, i: number): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = i === 0 ? "tool-action-btn primary" : "tool-action-btn";
  b.textContent = original.textContent ?? "";
  b.disabled = original.disabled;
  b.addEventListener("click", () => {
    // Der Knopf, der JETZT unten steht — der Abgleich kann die Karte neu gezeichnet haben.
    const ziel = angeheftet?.ziele[i] ?? original;
    if (!ziel.disabled) ziel.click();
    loeseRueckfrage();
  });
  return b;
}

/**
 * Die Rückfrage oben anheften, mit Ton.
 *
 * Gibt die Karte zurück, oder `null`, wenn es nichts (mehr) zu fragen gibt.
 *
 * Steht dieselbe schon oben, bleibt sie, wie sie ist: kein zweiter Ton,
 * keine zweite Karte. Das geschieht bei jedem Abgleich nach der Rückkehr
 * und beim Start, der zweimal abgleicht.
 */
export function zeigeRueckfrage(r: Rueckfrage, u: RueckfrageUmgebung): HTMLElement | null {
  const frage = (r.frage ?? "").trim();
  if (!frage || r.schluessel === erledigt()) return null;
  if (angeheftet?.schluessel === r.schluessel && angeheftet.karte.isConnected) {
    // Nur die Knöpfe unten können neu sein (der Abgleich zeichnet den Verlauf neu).
    if (r.antworten?.length) {
      angeheftet.ziele = r.antworten;
      stelleVerlaufskarten(r.antworten, "oben");
    }
    return angeheftet.karte;
  }
  loeseRueckfrage();

  const karte = document.createElement("section");
  karte.className = "rueckfrage-oben";
  karte.setAttribute("aria-label", "Offene Rückfrage");

  const kopf = document.createElement("div");
  kopf.className = "rueckfrage-oben__kopf";
  const marke = document.createElement("span");
  marke.className = "rueckfrage-oben__marke";
  marke.textContent = "Rückfrage";
  kopf.appendChild(marke);
  if (r.herkunft) {
    const herkunft = document.createElement("span");
    herkunft.className = "rueckfrage-oben__herkunft";
    herkunft.textContent = r.herkunft;
    kopf.appendChild(herkunft);
  }
  const weg = document.createElement("button");
  weg.type = "button";
  weg.className = "rueckfrage-oben__weg";
  weg.setAttribute("aria-label", "Rückfrage ausblenden");
  weg.title = "Ausblenden";
  weg.innerHTML = zeichen("schliessen", 18);
  weg.addEventListener("click", loeseRueckfrage);
  kopf.appendChild(weg);
  karte.appendChild(kopf);

  const text = document.createElement("p");
  text.className = "rueckfrage-oben__frage";
  text.textContent = frage;
  karte.appendChild(text);

  const leiste = document.createElement("div");
  leiste.className = "rueckfrage-oben__leiste tool-actions";
  const vorlesen = u.vorleseKnopf(frage);
  vorlesen.classList.add("rueckfrage-oben__vorlesen");
  leiste.appendChild(vorlesen);
  const ziele = r.antworten ?? [];
  ziele.forEach((b, i) => leiste.appendChild(spiegelKnopf(b, i)));
  karte.appendChild(leiste);

  platz().appendChild(karte);
  angeheftet = { karte, schluessel: r.schluessel, ziele };
  // Ja/Nein steht jetzt oben — die Karte im Verlauf zeigt nur „Offen“.
  stelleVerlaufskarten(ziele, "oben");
  u.ton();
  return karte;
}

/**
 * Die Rückfragen, auf die Martin schon geantwortet hat, als beantwortet
 * zeichnen. (pre.3598)
 *
 * Nach dem Neuladen oder auf dem anderen Gerät kennt die frisch gezeichnete
 * Karte ihre Antwort nicht — sie stünde mit lebendigen Knöpfen da, obwohl
 * längst geantwortet ist. Das steht in den Zeilen vom Server: Eine Karte ist
 * beantwortet, sobald nach ihrer Zeile eine Zeile von Martin kommt; deren
 * Wortlaut vor „ — auf deine Frage“ ist die Antwort.
 *
 * @param karten Die dabei neu gezeichneten Werkzeugkarten (callId → Karte).
 */
export function beantworteteKarten(
  zeilen: readonly VerlaufsZeile[],
  karten: ReadonlyMap<string, HTMLElement>,
): void {
  zeilen.forEach((zeile, i) => {
    for (const k of kartenDer(zeile)) {
      const unten = k.callId ? karten.get(k.callId) : undefined;
      if (!unten || !rueckfrageAusKarte(k)) continue;
      const danach = zeilen.slice(i + 1).find((z) => z.role === "user");
      if (!danach) continue;
      beantworteConfirmKarte(unten, danach.content.split(" — auf deine Frage")[0]);
    }
  });
}
